import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import DashboardShell from "../components/DashboardShell";
import { api, formatApiError } from "../lib/api";
import { Heart, CheckCircle, Loader2, Repeat } from "lucide-react";

const TIERS = ["10", "25", "50", "100"];

export default function DonatePage() {
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled");
  const [mode, setMode] = useState("once"); // "once" | "monthly"
  const [packageId, setPackageId] = useState("25");
  const [custom, setCustom] = useState("");
  const [anon, setAnon] = useState(false);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);
  const [paidAmount, setPaidAmount] = useState(null);
  const [donors, setDonors] = useState([]);
  const [myRecurring, setMyRecurring] = useState(null);

  useEffect(() => {
    api.get(`/donors/wall`).then((r) => setDonors(r.data.items || [])).catch(() => {});
    api.get(`/donations/recurring/me`).then((r) => setMyRecurring(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let stop = false;
    setPolling(true);
    const poll = async () => {
      if (stop || attempts >= 8) { setPolling(false); return; }
      try {
        const { data } = await api.get(`/donations/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setSuccess(true);
          setPaidAmount(data.amount);
          setPolling(false);
          toast.success("Donation received — thank you!");
          setTimeout(() => setParams({}, { replace: true }), 800);
          return;
        }
        if (data.status === "expired") { setPolling(false); toast.error("Checkout session expired."); return; }
      } catch (e) { console.error("status poll error", e); }
      attempts += 1;
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => { if (cancelled) toast("Checkout cancelled — try again whenever you're ready."); }, [cancelled]);

  const donate = async () => {
    setErr(""); setBusy(true);
    try {
      const customAmt = custom ? parseFloat(custom) : null;
      if (custom && (!customAmt || customAmt <= 0)) throw new Error("Enter a valid custom amount.");

      if (mode === "monthly") {
        // Sign up for recurring intent + run a single checkout NOW to confirm the first month
        const recurringBody = { package_id: customAmt ? "25" : packageId, custom_amount_usd: customAmt };
        await api.post(`/donations/recurring/signup`, recurringBody);
        toast.success("You're set up as a monthly donor. Charging your first month now…");
        const r = await api.get(`/donations/recurring/me`);
        setMyRecurring(r.data);
      }
      const body = {
        package_id: custom ? "custom" : packageId,
        custom_amount_usd: customAmt,
        is_anonymous: anon,
        origin_url: window.location.origin,
      };
      const { data } = await api.post(`/donations/checkout`, body);
      window.location.href = data.checkout_url;
    } catch (e) {
      const msg = formatApiError(e) || e.message;
      setErr(msg); toast.error(msg);
    } finally { setBusy(false); }
  };

  const cancelRecurring = async () => {
    if (!window.confirm("Cancel your monthly donation?")) return;
    try { await api.post(`/donations/recurring/cancel`); toast.success("Cancelled. Thank you for what you've given so far."); const r = await api.get(`/donations/recurring/me`); setMyRecurring(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (success) return (
    <DashboardShell>
      <div className="max-w-xl mx-auto csc-card p-10 text-center" data-testid="donate-success">
        <CheckCircle size={56} className="text-[#39ff14] mx-auto" />
        <h1 className="text-2xl font-semibold mt-4">Thank you!</h1>
        <p className="text-[#94a3b8] mt-2">Your {paidAmount ? `$${paidAmount.toFixed(2)} ` : ""}donation keeps nonprofits secure. A receipt has been emailed to you.</p>
        <a href="/home" className="csc-btn-primary inline-flex mt-6" data-testid="back-home-donate">Back to feed</a>
      </div>
    </DashboardShell>
  );

  return (
    <DashboardShell>
      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6" data-testid="donate-page">
        <div className="csc-card p-8 space-y-5">
          <div className="text-center">
            <Heart className="text-[#39ff14] mx-auto" size={36} />
            <h1 className="text-3xl font-semibold mt-2">Power the Mission</h1>
            <p className="text-[#94a3b8] mt-1">Every dollar funds free cybersecurity tools, training, and incident response for charities.</p>
          </div>

          {polling && (
            <div className="csc-card p-3 text-xs text-[#00aaff] flex items-center gap-2" data-testid="donate-polling">
              <Loader2 className="animate-spin" size={14} /> Confirming payment with Stripe…
            </div>
          )}
          {cancelled && !success && (
            <div className="csc-card p-3 text-xs text-amber-300" data-testid="donate-cancelled">Checkout cancelled — try again whenever you're ready.</div>
          )}

          {myRecurring?.active && (
            <div className="csc-card csc-card-green p-4 flex items-center justify-between gap-3" data-testid="my-recurring-banner">
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#39ff14]">// Monthly Donor</div>
                <div className="text-sm mt-1">You're giving <span className="font-bold text-[#39ff14]">${myRecurring.amount_usd?.toFixed(2)}</span> each month. Next charge: {myRecurring.next_charge_at ? new Date(myRecurring.next_charge_at).toLocaleDateString() : "—"}.</div>
              </div>
              <button onClick={cancelRecurring} className="csc-btn-ghost !py-1.5 !text-xs" data-testid="cancel-recurring">Cancel</button>
            </div>
          )}

          {/* Mode toggle */}
          <div className="flex gap-2" data-testid="donate-mode-toggle">
            <button onClick={() => setMode("once")} className={`flex-1 csc-btn-ghost ${mode==="once" ? "!border-[#00aaff] !text-[#00aaff] !bg-[rgba(0,170,255,0.12)]" : ""}`} data-testid="mode-once">One-time</button>
            <button onClick={() => setMode("monthly")} className={`flex-1 csc-btn-ghost ${mode==="monthly" ? "!border-[#39ff14] !text-[#39ff14] !bg-[rgba(57,255,20,0.12)]" : ""}`} data-testid="mode-monthly">
              <Repeat size={12} className="mr-1" /> Monthly
            </button>
          </div>

          <div className="grid grid-cols-4 gap-2">
            {TIERS.map((t) => (
              <button key={t} onClick={() => { setPackageId(t); setCustom(""); }} className={`csc-card p-4 text-center text-xl font-semibold ${packageId === t && !custom ? (mode==="monthly"?"!border-[#39ff14] !text-[#39ff14]":"!border-[#00aaff] !text-[#00aaff]") : ""}`} data-testid={`tier-${t}`}>${t}</button>
            ))}
          </div>
          <div>
            <label className="csc-label">Custom amount (USD)</label>
            <input type="number" min="1" value={custom} onChange={(e) => setCustom(e.target.value)} className="csc-input" placeholder="Other amount" data-testid="custom-amount" />
          </div>
          {mode === "once" && (
            <label className="flex items-center gap-2 text-xs text-[#94a3b8]">
              <input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} className="accent-[#00aaff]" data-testid="anon-toggle" />
              Donate anonymously
            </label>
          )}
          {err && <div className="text-xs text-red-400" data-testid="donate-error">{err}</div>}
          <button onClick={donate} disabled={busy} className={mode==="monthly" ? "csc-btn-secondary w-full csc-pulse" : "csc-btn-primary w-full csc-pulse"} data-testid="donate-submit">
            {busy ? "Redirecting…" : mode === "monthly" ? `Donate $${custom || packageId} / month` : `Donate $${custom || packageId}`}
          </button>
          <p className="text-[10px] font-mono-tech text-center text-[#94a3b8]">Powered by Stripe (test mode) · CyberSecurity4Charities.org never stores card details.</p>
        </div>

        {/* Donor wall */}
        <aside className="csc-card p-5" data-testid="donor-wall">
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14]">// Donor Wall</div>
          <h2 className="text-base font-semibold mt-1 mb-3">Recent Heroes</h2>
          {donors.length === 0 ? (
            <p className="text-xs text-[#94a3b8]">Be the first to power the mission.</p>
          ) : (
            <ul className="space-y-2.5">
              {donors.slice(0, 12).map((d, i) => (
                <li key={i} className="flex items-center justify-between gap-2 text-sm" data-testid={`donor-${i}`}>
                  <div className="min-w-0">
                    <div className="truncate">{d.name}</div>
                    <div className="font-mono-tech text-[10px] text-[#94a3b8]">{d.donations}× · {d.last_donation_at ? new Date(d.last_donation_at).toLocaleDateString() : ""} {d.is_recurring && <span className="text-[#39ff14]">· monthly</span>}</div>
                  </div>
                  <div className="font-bold text-[#39ff14]">${d.amount_usd.toFixed(0)}</div>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </DashboardShell>
  );
}
